import React, { useState } from "react";
import { CircularProgress } from "@mui/material";
import AddTextLocation from "./AddPostcode.tsx";
import EnteredLocation from "./EnteredLocation.tsx";
import Location from "./Location.ts";
import MaxMinutesSlider from "./MaxMinutesSlider.tsx";

// The single always-visible card: starting locations and departure time.
// `embedded` drops the card chrome for rendering inside the bottom sheet,
// and inlines the travel-time picker when maxMinutes/setMaxMinutes are
// given (on desktop the picker lives behind the legend instead).
const ControlPanel = ({
  locations,
  addLoc,
  deleteLoc,
  time,
  setTime,
  backendAwake,
  journeysLoading,
  embedded = false,
  maxMinutes,
  setMaxMinutes,
}: {
  locations: Location[];
  addLoc: (loc: Location) => void;
  deleteLoc: (idx: number) => void;
  time: string;
  setTime: (time: string) => void;
  backendAwake: boolean;
  journeysLoading: boolean;
  embedded?: boolean;
  maxMinutes?: number;
  setMaxMinutes?: (mins: number) => void;
}) => {
  const [newLocError, setNewLocError] = useState("");

  const onNewLoc = (location: Location) => {
    if (locations.some((l) => l.name === location.name)) {
      setNewLocError("No duplicates");
      return;
    }
    setNewLocError("");
    addLoc(location);
  };

  return (
    <div
      className={
        embedded
          ? ""
          : "rounded-xl border border-gray-200 bg-white/95 p-4 shadow-lg backdrop-blur"
      }
    >
      <h1 className="text-base font-semibold text-gray-800">
        London Travel Time
      </h1>
      <p className="!m-0 pb-3 text-xs text-gray-400">
        Add starting points — the map shows how long it takes to get anywhere.
      </p>

      {locations.map((l, i) => (
        <EnteredLocation key={l.name} location={l} idx={i} onDelete={deleteLoc} />
      ))}
      <AddTextLocation onClick={onNewLoc} error={newLocError} />

      <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3">
        <label className="text-sm text-gray-500">Departure</label>
        <input
          type="time"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          className="rounded-lg border border-gray-200 px-2 py-1 text-sm text-gray-600 focus:outline-none"
        />
      </div>

      {maxMinutes !== undefined && setMaxMinutes && (
        <div className="mt-3 border-t border-gray-100 pt-3">
          <MaxMinutesSlider
            maxMinutes={maxMinutes}
            setMaxMinutes={setMaxMinutes}
          />
        </div>
      )}

      {!backendAwake && (
        <div className="mt-3 flex items-center gap-2 border-t border-gray-100 pt-3 text-xs text-gray-400">
          <CircularProgress size="0.9rem" />
          Waking the server — this can take a few minutes.
        </div>
      )}
      {backendAwake && journeysLoading && (
        <div className="mt-3 flex items-center gap-2 border-t border-gray-100 pt-3 text-xs text-gray-400">
          <CircularProgress size="0.9rem" />
          Computing travel times…
        </div>
      )}
    </div>
  );
};

export default ControlPanel;
